import { atomFamily } from 'jotai/utils';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';
import { useAtom } from 'jotai';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ChevronDown, ChevronUp } from '@mynaui/icons-react';

import { StationVolumesChip } from '@/components/StationVolumesChip';
import { DOCK_PAD, DockKpi, DockPanel } from '@/components/dock/DockHeaderParts';
import { DockPanelActions } from '@/components/dock/DockPanelActions';
import { SV_TOKENS } from '@/lib/stationVolumes/tokens';
import type { StationVolumesLayerMeta } from '@/lib/stationVolumes/types';

import { cn } from '@/lib/utils';
import { formatM2, formatM3, formatM3Row } from '@/lib/stationVolumes/format';

/**
 * Renders the volume integration metrics computed by `scripts/deviation.py`
 * (Approach 1, raster integration) on the derived POINTCLOUD layer's
 * `metadata.deviationStats.volume`.
 *
 * TWO display states, the same two `SurfaceDifferencePanel` has:
 *   - **collapsed**: one-liner with net volume + tolerance %. Tiny
 *     footprint so it doesn't crowd the colorbar.
 *   - **expanded** (default): full KPI grid + worst-case footer.
 *     Surfaces-mode adds a per-surface table. These cards exist to show
 *     three numbers, and this panel shares a column with
 *     `SurfaceDifferencePanel`, which defaults open — defaulting this one
 *     shut meant its figures went unseen next to a neighbour showing
 *     its own.
 *
 * There WAS a third, `dismissed`, reached from an X in the header and undone
 * by a "Show volume" pill floating where the card had been. It is gone: it
 * was a whole hide/restore mechanic that only one of the two panels in the
 * column had, and collapsing already gets the card down to a single header
 * line. `panelStateStorage` below deals with the users who are still carrying
 * the old value in localStorage.
 *
 * The collapsed/expanded state is per-layer-id and stored in localStorage,
 * matching `SurfaceDifferencePanel`'s panel state, so a collapse survives a
 * browser restart the same way on both cards — one remembering across
 * restarts and the other only for the tab was its own source of visible
 * drift between them.
 */

interface SurfaceStats {
  featureId?: number;
  featureName?: string;
  pointCount?: number;
  positiveVolumeM3?: number;
  negativeVolumeM3?: number;
  netVolumeM3?: number;
  coverageAreaM2?: number;
  withinTolerancePct?: number;
}

/** Exported so `DeviationVolumePanel.dom.test.tsx` can type its fixture
 *  against the REAL prop type. Typing it as `typeof VOLUME` instead — the
 *  shape of one literal — meant a test could not add a field the component
 *  genuinely accepts, and the mismatch surfaced as a tsc error rather than
 *  as a useful signal. */
export interface VolumeStats {
  positiveVolumeM3?: number;
  negativeVolumeM3?: number;
  netVolumeM3?: number;
  coverageAreaM2?: number;
  withinTolerancePct?: number;
  withinToleranceMM?: number;
  worstPositiveDeviationM?: number;
  worstPositiveX?: number;
  worstPositiveY?: number;
  worstPositiveZ?: number;
  worstNegativeDeviationM?: number;
  worstNegativeX?: number;
  worstNegativeY?: number;
  worstNegativeZ?: number;
  rasterPlane?: 'xy' | 'fitted' | string;
  rasterResolutionM?: number;
  referenceMode?: 'tin' | 'surfaces' | string;
  /**
   * T1. The volumes above integrate every point that matched the reference,
   * not only those inside the min/max colour range — so the range's remaining
   * job is colouring, filtering the written cloud and scoping the tolerance
   * figure. `outOfRangePointPct` is how much of the measured cloud that band
   * leaves out, and the panel prints it whenever it is more than none: a
   * reader who set ±5 cm has otherwise no way to see that a fifth of the
   * cloud is nowhere near it.
   *
   * All three are absent on runs made before the worker wrote them, which is
   * why the panel renders nothing rather than "— %" when they are missing.
   */
  volumePointCount?: number;
  outOfRangePointCount?: number;
  outOfRangePointPct?: number;
  /**
   * What those points are WORTH — the same cell-mean integration restricted to
   * them, both as magnitudes. A share of points cannot tell a reader the
   * volume impact, and the worker's vertical pre-filter pad is generous enough
   * (10 m) that vegetation and noise land in the measured set alongside
   * genuine overbuild. Ship together with `outOfRangePointPct`.
   */
  outOfRangeCutVolumeM3?: number;
  outOfRangeFillVolumeM3?: number;
  surfaces?: SurfaceStats[];
}

type PanelState = 'collapsed' | 'expanded';

/** What may be sitting in localStorage: the two live states, plus the
 *  `dismissed` this panel wrote for as long as it had an X button. */
type StoredPanelState = PanelState | 'dismissed';

const jsonStorage = createJSONStorage<StoredPanelState>(() => localStorage);

/**
 * Migrates a stored `dismissed` to `collapsed`, on read.
 *
 * Removing the X button removed the state, but not the VALUES already written
 * by it: this key is `localStorage`, so every user who ever dismissed this
 * panel still has `"dismissed"` under it, on every machine they use, until
 * something rewrites it. Nothing would: the panel no longer writes that
 * string, so the stale value survives every visit and the atom keeps handing
 * back a `PanelState` that is not one — `state` is neither `'expanded'` nor
 * `'collapsed'`, which every `=== 'expanded'` test in this file quietly reads
 * as "collapsed" and no `=== 'collapsed'` test would.
 *
 * So it is corrected on READ and WRITTEN BACK, which is what makes it a
 * migration rather than a permanent translation layer: the first mount after
 * this ships clears the value for good, and the stale string cannot outlive
 * the code that understood it. Read-through (rather than a one-off sweep at
 * module load) because `atomFamily` caches its atoms forever — a sweep in the
 * factory would run once per layer id per PAGE LOAD, and never for a value
 * another tab on the old build wrote afterwards.
 */
const panelStateStorage = {
  getItem: (key: string, initialValue: PanelState): PanelState => {
    const stored = jsonStorage.getItem(key, initialValue);
    if (stored !== 'dismissed') return stored;
    jsonStorage.setItem(key, 'collapsed');
    return 'collapsed';
  },
  setItem: (key: string, newValue: PanelState) => jsonStorage.setItem(key, newValue),
  removeItem: (key: string) => jsonStorage.removeItem(key),
  subscribe: (key: string, callback: (value: PanelState) => void, initialValue: PanelState) =>
    jsonStorage.subscribe?.(key, (v) => callback(v === 'dismissed' ? 'collapsed' : v), initialValue),
};

/**
 * Per-layer panel state, persisted across browser restarts. Defaults to
 * `expanded`, matching `SurfaceDifferencePanel` right below it in the same
 * column: both cards show a three-figure KPI row, and defaulting only one
 * of them shut is what read as "these two panels are completely
 * different" in practice — the taller card never showed its figures.
 */
const volumePanelStateAtom = atomFamily((layerId: string) =>
  atomWithStorage<PanelState>(`@actegon.gis.deviation.volume.${layerId}`, 'expanded', panelStateStorage),
);

interface Props {
  projectId: string;
  layerId: string;
  layerName: string;
  volume: VolumeStats;
  /** The station breakdown, when the run asked for one (spec §10.4). */
  stationVolumes?: StationVolumesLayerMeta | null;
}

/**
 * WIRE → DISPLAY, and the only place this panel converts between the two.
 *
 * `deviation.py` stores the fill volume SIGNED NEGATIVE and computes
 * `net_volume = pos_volume + neg_volume`, i.e. `Cut − Fill`, positive for a
 * net cut. Every other surface in this app states the same quantity the other
 * way round: `surface_difference.py` returns `netVolumeM3 = fill - cut`, and
 * both station-volume docks print `Net = Fill − Cut` under a legend that reads
 * "Fill, above the line / Cut, below the line". Measured together in a browser
 * on 2026-09-05, this panel was one against three — and it disagreed with its
 * OWN station-volumes dock, calling the same analysis's net cut `+253.8 m³` in
 * the card and `−184.1 m³` in the dock directly below it.
 *
 * So the rule for the whole panel is now:
 *   - **Cut and Fill are magnitudes.** The direction is in the label, never in
 *     the sign; a row that says "Fill" and shows `−6.9` is asking the reader to
 *     apply the sign twice.
 *   - **Net is the one signed figure**, because its sign IS the answer:
 *     positive = net fill, negative = net cut.
 *
 * Nothing on the wire or in storage changes — this negates the stored net
 * rather than recomputing it, so the worker's own arithmetic still decides the
 * number and only its orientation is flipped here.
 */
const toDisplayTrio = (stats: {
  positiveVolumeM3?: number;
  negativeVolumeM3?: number;
  netVolumeM3?: number;
}): (number | undefined)[] => {
  const abs = (v: number | undefined) => (v == null || !Number.isFinite(v) ? undefined : Math.abs(v));
  const net = stats.netVolumeM3;
  return [abs(stats.positiveVolumeM3), abs(stats.negativeVolumeM3), net == null || !Number.isFinite(net) ? undefined : -net];
};

/**
 * A single cubic-metre figure on the shared magnitude rule (`format.ts`): 3
 * decimals below 100 m³, 1 above, none from 10 000 m³ — the panel used to print
 * +30000.123 m³.
 *
 * No explicit `+`. `SurfaceDifferencePanel` prints `1086.3 m³`, not `+1086.3
 * m³`, and these two cards stack in one column: a plus sign on one and not the
 * other was among the "different styles" the owner reported. A minus still
 * comes through from `formatM3`, which is the only sign that carries meaning
 * once Cut and Fill are magnitudes.
 */
const formatVolume = (m3: number | undefined): string => {
  if (m3 == null || !Number.isFinite(m3)) return '—';
  return `${formatM3(m3)} m³`;
};

/**
 * A Cut / Fill / Net trio at ONE precision, chosen from the largest figure —
 * the same rule, and the same helper, `SurfaceDifferencePanel` applies to its
 * own trio. Takes DISPLAY numbers (see `toDisplayTrio`), never wire ones.
 */
const formatVolumeRow = (values: (number | undefined)[]): string[] => {
  const nums = values.map((v) => (v == null || !Number.isFinite(v) ? 0 : v));
  const row = formatM3Row(nums);
  return values.map((v, i) => (v == null || !Number.isFinite(v) ? '—' : `${row[i]} m³`));
};

/**
 * A worst-case DEVIATION as a magnitude. Same rule as the volumes above: the
 * label says "Worst cut" or "Worst fill", so the number does not also need a
 * sign to say which way it went — and `−0.564 m` under a label reading "Worst
 * fill" reads as a negative amount of fill, which is not a thing.
 */
const formatWorst = (m: number | undefined): string => formatDeviation(m, false);

/**
 * Total material outside the colour range: cut + fill, both already
 * magnitudes on the wire. `undefined` when neither figure is present, which is
 * what an old run looks like — `formatVolume` then prints an em dash rather
 * than a confident 0.
 */
const outOfRangeTotal = (stats: {
  outOfRangeCutVolumeM3?: number;
  outOfRangeFillVolumeM3?: number;
}): number | undefined => {
  const parts = [stats.outOfRangeCutVolumeM3, stats.outOfRangeFillVolumeM3].filter(
    (v): v is number => v != null && Number.isFinite(v),
  );
  return parts.length ? parts.reduce((a, b) => a + b, 0) : undefined;
};

const formatArea = (m2: number | undefined): string => {
  if (m2 == null || !Number.isFinite(m2)) return '—';
  return `${formatM2(m2)} m²`;
};

const formatPct = (pct: number | undefined): string => {
  if (pct == null || !Number.isFinite(pct)) return '—';
  return `${pct.toFixed(1)} %`;
};

/**
 * An elevation difference on the magnitude-appropriate unit: mm below 5 mm, cm
 * below 500 mm, metres above. `signed` is opt-in — see `formatWorst`, which
 * wants the bare magnitude because its label already says which way the
 * deviation went.
 */
const formatDeviation = (m: number | undefined, signed = true): string => {
  if (m == null || !Number.isFinite(m)) return '—';
  const abs = Math.abs(m);
  const sign = !signed ? '' : m > 0 ? '+' : m < 0 ? '−' : '';
  if (abs < 0.005) return `${sign}${(abs * 1000).toFixed(1)} mm`;
  if (abs < 0.5) return `${sign}${(abs * 100).toFixed(2)} cm`;
  return `${sign}${abs.toFixed(3)} m`;
};

const formatCoord = (n: number | undefined): string => (n != null && Number.isFinite(n) ? n.toFixed(2) : '—');

export function DeviationVolumePanel({ projectId, layerId, layerName, volume, stationVolumes }: Props) {
  const { t } = useTranslation('project');
  const [state, setState] = useAtom(volumePanelStateAtom(layerId));
  // Local UI for the per-surface sub-section: collapsed by default even
  // when the parent is expanded, since the table can grow long for
  // multi-wall projects.
  const [surfacesOpen, setSurfacesOpen] = useState(false);

  if (!volume) return null;

  const toleranceMm = volume.withinToleranceMM ?? 10;
  const isExpanded = state === 'expanded';
  const surfaces = volume.surfaces ?? [];

  // Cut / Fill / Net at ONE precision, picked from the largest of the three —
  // the same rule `SurfaceDifferencePanel` applies to its own trio. Wire values
  // are oriented for display FIRST (`toDisplayTrio`), so the figures here and
  // the Net in the collapsed summary below cannot disagree about which way
  // round the convention runs.
  const displayTrio = toDisplayTrio(volume);
  const kpiRow = formatVolumeRow(displayTrio);

  return (
    <>
      {/* `.sv-palette` brings in the station-volumes `--sv-*` variables (light
          and dark) with no surface of its own, so the figures below are inked
          from the same palette as the section, the colorbar and the 3D bands —
          the surface panel below this one already does exactly this. */}
      <style>{SV_TOKENS}</style>
      {/* Chrome (surface, padding, header layout, chevron, collapse target) is
          `DockPanel`'s, shared with `SurfaceDifferencePanel` right below this
          one in the same column — see its doc comment for why each value is
          what it is. */}
      <DockPanel
        className='sv-palette'
        expanded={isExpanded}
        onToggle={() => setState(isExpanded ? 'collapsed' : 'expanded')}
        toggleLabel={isExpanded ? t('dockPanel.collapse') : t('dockPanel.expand')}
        title={t('dockPanel.title')}
        summary={
          !isExpanded && (
            <>
              <span className='ml-2 shrink-0 font-mono tabular-nums whitespace-nowrap text-slate-700 dark:text-stone-200'>
                {formatVolume(displayTrio[2])}
              </span>
              {volume.withinTolerancePct != null && (
                <span className='ml-2 shrink-0 whitespace-nowrap text-slate-500 dark:text-stone-400'>
                  · {formatPct(volume.withinTolerancePct)}
                </span>
              )}
            </>
          )
        }
        actions={
          // Layer name + PDF button, from the ONE component both panels use —
          // see `DockPanelActions` for why this is not markup living in two
          // files any more. The X (dismiss) that used to sit at the end of this
          // row is gone with the state behind it.
          <DockPanelActions projectId={projectId} layerId={layerId} layerName={layerName} kind='deviation' />
        }>
        {isExpanded && (
          <div className='mt-1'>
            {/* The three volumes as `DockKpi` — the shared figure primitive
                `ClearanceDock`, `StationVolumeDock` and `SurfaceDifferencePanel`
                all use, so a figure looks the same wherever this app prints one.
                This was a `<dl>` of right-aligned mono values, which put the
                deviation figures in a different style AND a different position
                from the cut/fill card directly below it in the same column.

                The LABELS now match `SurfaceDifferencePanel`'s exactly: Cut,
                Fill, Net, same order, same casing. Material sitting ABOVE the
                design is what a machine cuts away; material MISSING below it
                is what it fills — the same physical quantity the cut/fill card
                reports between two surfaces, so the two cards now share the
                vocabulary as well as the presentation.

                The inks are the shared `--sv-cut` / `--sv-fill` variables
                rather than emerald/rose, which were a fourth ad-hoc pair for a
                quantity this feature already has a palette for: positive
                takes the cut hue and negative the fill hue, and the same
                material carries one colour across both cards. `items-end`
                keeps the figures in line when a label wraps in a narrow
                column. */}
            <div className='grid grid-cols-3 items-end gap-2'>
              <DockKpi label={t('dockPanel.cut')} value={kpiRow[0]} color='var(--sv-cut)' />
              <DockKpi label={t('dockPanel.fill')} value={kpiRow[1]} color='var(--sv-fill)' />
              <DockKpi label={t('dockPanel.net')} value={kpiRow[2]} />
            </div>

            {/* The chip sits AFTER the figures, as it does in
                `SurfaceDifferencePanel`. It used to render BEFORE them, which
                pushed this card's Cut/Fill/Net row below the station-volumes
                box while the surface card showed its row directly under the
                header — the same three figures in two different places in the
                same column. The chip is one shared component, so only the
                ORDER could drift, and it did. */}
            {stationVolumes && (
              <StationVolumesChip layerId={layerId} kind='deviation' summary={stationVolumes.summary} />
            )}

            {/* ONE label/value grid, the same one `SurfaceDifferencePanel`
                uses for its Area / Coverage / Resolution rows. Resolution and
                plane used to live below this in a separate 11px `opacity-70`
                footer strip, so the two cards put the same class of fact in two
                different places at two different weights. They are rows here
                now; the panels differ only where the QUANTITIES genuinely do
                (this one judges against a tolerance, that one reports grid
                coverage), which is the only difference worth seeing.

                Resolution is in metres on both cards. This one printed
                `5.0 cm` while the surface card printed `0.5 m` for the same
                kind of number, and metres is what the run dialog asks the user
                to type ("Grid resolution (m)"). */}
            <div className='mt-2 grid grid-cols-2 gap-1 text-slate-600 dark:text-stone-300'>
              <span>{t('dockPanel.area')}</span>
              <span className='text-right'>{formatArea(volume.coverageAreaM2)}</span>
              <span>{t('deviationVolume.withinTolerance', { tolerance: toleranceMm.toFixed(0) })}</span>
              <span className='text-right'>{formatPct(volume.withinTolerancePct)}</span>
              {volume.rasterResolutionM != null && (
                <>
                  <span>{t('dockPanel.resolution')}</span>
                  <span className='text-right'>{volume.rasterResolutionM.toFixed(2)} m</span>
                </>
              )}
              {volume.rasterPlane != null && (
                <>
                  <span>{t('deviationVolume.plane')}</span>
                  <span className='text-right'>{volume.rasterPlane}</span>
                </>
              )}
            </div>

            {/* T1: how much of the measured cloud the colour range leaves
                out, and what it is worth. The figures above already include
                it — this line exists so a tight range reads as a display
                choice rather than as the extent of what was measured, and so
                that "21 % of points" cannot be mistaken for "21 % of the
                volume". Cut and fill are magnitudes, so the m³ is their sum:
                total material sitting outside the band, whichever way it
                went. */}
            {volume.outOfRangePointPct != null &&
              Number.isFinite(volume.outOfRangePointPct) &&
              volume.outOfRangePointPct > 0 && (
                <p
                  data-testid='dev-out-of-range'
                  title={t('deviationAnalysis.outOfRangeShareHelp')}
                  className='mt-1 text-[11px] text-slate-500 dark:text-stone-400'>
                  {t('deviationAnalysis.outOfRangeShare', {
                    pct: volume.outOfRangePointPct.toFixed(1),
                    volume: formatVolume(outOfRangeTotal(volume)),
                  })}
                </p>
              )}

            {(volume.worstPositiveDeviationM != null || volume.worstNegativeDeviationM != null) && (
              <div className={cn(DOCK_PAD.section, 'text-[11px] text-slate-500 dark:text-stone-400')}>
                {volume.worstPositiveDeviationM != null && (
                  <div className='flex items-center justify-between gap-3'>
                    <span>{t('deviationVolume.worstCut')}</span>
                    <span className='font-mono tabular-nums'>
                      {formatWorst(volume.worstPositiveDeviationM)}
                      <span className='ml-1 opacity-70'>
                        ({formatCoord(volume.worstPositiveX)}, {formatCoord(volume.worstPositiveY)},{' '}
                        {formatCoord(volume.worstPositiveZ)})
                      </span>
                    </span>
                  </div>
                )}
                {volume.worstNegativeDeviationM != null && (
                  <div className='flex items-center justify-between gap-3'>
                    <span>{t('deviationVolume.worstFill')}</span>
                    <span className='font-mono tabular-nums'>
                      {formatWorst(volume.worstNegativeDeviationM)}
                      <span className='ml-1 opacity-70'>
                        ({formatCoord(volume.worstNegativeX)}, {formatCoord(volume.worstNegativeY)},{' '}
                        {formatCoord(volume.worstNegativeZ)})
                      </span>
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Per-surface breakdown — only for `surfaces` mode. Collapsed by
                default to keep the panel compact; users with 1–3 surfaces
                will expand often, users with 20+ won't. The full table is
                also downloadable as a CSV byproduct (registered server-side
                with the `volumes-…csv` filename). */}
            {surfaces.length > 0 && (
              <div className={DOCK_PAD.section}>
                <button
                  type='button'
                  onClick={() => setSurfacesOpen((v) => !v)}
                  className='flex w-full items-center gap-1 text-left text-slate-700 dark:text-stone-200'>
                  {surfacesOpen ? <ChevronUp className='size-3' /> : <ChevronDown className='size-3' />}
                  <span>{t('deviationVolume.perSurface', { count: surfaces.length })}</span>
                </button>
                {surfacesOpen && (
                  <div className='mt-1.5 max-h-56 overflow-auto'>
                    <table className='w-full text-[11px]'>
                      <thead className='text-slate-600 dark:text-stone-300'>
                        <tr>
                          <th className={cn(DOCK_PAD.cell, 'text-left font-medium')}>
                            {t('deviationVolume.tableSurface')}
                          </th>
                          {/* Same two inks as the KPI row above: the `+` column
                              is the cut quantity, the `−` column the fill
                              one. */}
                          <th
                            className={cn(DOCK_PAD.cell, 'text-right font-medium')}
                            style={{ color: 'var(--sv-cut)' }}>
                            +
                          </th>
                          <th
                            className={cn(DOCK_PAD.cell, 'text-right font-medium')}
                            style={{ color: 'var(--sv-fill)' }}>
                            −
                          </th>
                          <th className={cn(DOCK_PAD.cell, 'text-right font-medium')}>
                            {t('deviationVolume.tableNet')}
                          </th>
                          <th className={cn(DOCK_PAD.cell, 'text-right font-medium')}>
                            {t('deviationVolume.tableTol')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {surfaces.map((s, i) => (
                          <tr
                            key={s.featureId ?? i}
                            className={cn(
                              'border-t border-slate-100 dark:border-stone-800',
                              i % 2 === 1 && 'bg-slate-50/40 dark:bg-stone-900/40',
                            )}>
                            <td className={cn(DOCK_PAD.cell, 'truncate')} title={s.featureName ?? ''}>
                              {s.featureName ?? `#${s.featureId ?? i}`}
                            </td>
                            <td
                              className={cn(DOCK_PAD.cell, 'text-right font-mono tabular-nums')}
                              style={{ color: 'var(--sv-cut)' }}>
                              {formatVolumeRow(toDisplayTrio(s))[0]}
                            </td>
                            <td
                              className={cn(DOCK_PAD.cell, 'text-right font-mono tabular-nums')}
                              style={{ color: 'var(--sv-fill)' }}>
                              {formatVolumeRow(toDisplayTrio(s))[1]}
                            </td>
                            <td className={cn(DOCK_PAD.cell, 'text-right font-mono tabular-nums')}>
                              {formatVolumeRow(toDisplayTrio(s))[2]}
                            </td>
                            <td className={cn(DOCK_PAD.cell, 'text-right font-mono tabular-nums')}>
                              {formatPct(s.withinTolerancePct)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </DockPanel>
    </>
  );
}
